/**
 * The Quest's reveals (quest-journey-2026-10-27).
 *
 * A constellation plays where the person earned it, over the screen they are
 * on, at the natural end of the act: after a reading closes, never
 * mid-reading or mid-interview. Earned while away, it plays on the next app
 * open, wherever they land. Each reveal runs the same way (DESIGN "Each
 * reveal"): the section's own animation; a light flies to the bar, which
 * makes room first, then the tab is born or changes its face as the light
 * lands; then a hero whose "Get started" lands INSIDE the new room.
 *
 * The server says which are due (`nav.reveals`, oldest first) and forgets one
 * on `nav/seen/ reveal:<key>`.
 */
import { sectionKey } from '@/utils/quest-sky'

/**
 * Per constellation: the bar slot it lands on and how (born, a face change,
 * or a ring on a tab already there), and the room "Get started" opens.
 */
export const REVEALS = {
  fs: { to: 'journal', change: 'born' },
  ri: { to: 'mirror', from: 'journal', change: 'upgr' },
  ve: { to: 'mirror', change: 'ring' },
  zw: { to: 'community', from: 'mystic', change: 'upgr' },
  mo: { to: 'altar', from: 'mirror', change: 'upgr' },
  su: { to: 'studio', change: 'born' },
}

const ROOM_OF = { fs: 'journal', ri: 'mirror', ve: 'circle', zw: 'community', mo: 'altar', su: 'studio' }
const OF_ROOM = Object.fromEntries(Object.entries(ROOM_OF).map(([k, v]) => [v, k]))
export const roomOf = (key) => ROOM_OF[key] || ''

/**
 * The due reveal (`nav.unlock`, API §1.2/§2) as [{ key: 'ri', seen: 'unlock:the_rising',
 * hero: 'hero:mirror', plays, saved, … }]. Also takes a list (older drafts).
 */
export function readReveals (nav) {
  if (!nav) return []
  const list = Array.isArray(nav.reveals) ? nav.reveals : (nav.unlock ? [nav.unlock] : [])
  const out = []
  for (const r of list) {
    const raw = typeof r === 'string' ? { key: r } : (r && typeof r === 'object' ? r : null)
    if (!raw) continue
    const key = sectionKey(raw.constellation || String(raw.key || '').replace(/^unlock:/, ''))
    if (!key || !REVEALS[key]) continue
    const apiKey = raw.constellation || String(raw.key || '').replace(/^unlock:/, '')
    out.push({ ...raw, key, apiKey, seen: `unlock:${apiKey}`, hero: `hero:${ROOM_OF[key]}`, plays: raw.plays || (key === 'su' ? 'quest_or_altar' : 'where_you_are') })
  }
  return out
}

/** A hero still owed with no reveal to play first (`nav.heroes_due`): show it alone. */
export function heroDue (nav) {
  const rooms = nav && Array.isArray(nav.heroes_due) ? nav.heroes_due : (nav && Array.isArray(nav.heroesDue) ? nav.heroesDue : [])
  for (const room of rooms) {
    const key = OF_ROOM[room]
    if (key) return { key, apiKey: room, heroOnly: true, hero: `hero:${room}` }
  }
  return null
}

/** The one to play now: the oldest not yet played this visit. */
export function dueReveal (reveals, played = new Set()) {
  return (reveals || []).find((r) => !played.has(r.key)) || null
}

/** At the natural end of an act only. */
export function revealMayPlay ({ signedIn = false, inReading = false, interviewing = false, introRoom = false, busy = false } = {}) {
  return !!signedIn && !inReading && !interviewing && !introRoom && !busy
}

/**
 * The slot change for a reveal: the server's `unlock.bar` ({slot, to, from})
 * when it sends one (rooms open in any order since 2026-10-30, so the Journal
 * can land under an open Mirror: a ring), else the built-in path. `from` null:
 * the tab is born; `from === to`: a ring; else the slot changes its face.
 */
function specFor (reveal) {
  const spec = REVEALS[reveal.key]
  const b = reveal.bar
  if (!spec || !b || typeof b !== 'object' || !b.to || !(b.to in FACES)) return spec
  if (b.from && b.from !== 'mystic' && !(b.from in FACES)) return spec
  if (!b.from) return { to: b.to, change: 'born' }
  if (b.from === b.to) return { to: b.to, change: 'ring' }
  return { to: b.to, from: b.from, change: 'upgr' }
}

// The face a slot shows before the light lands.
const FACES = {
  journal: { label: 'Journal', icon: 'fa-book-open' },
  mirror: { label: 'Mirror', icon: '' },
  altar: { label: 'Altar', icon: 'fa-eye' },
  community: { label: 'Community', icon: 'fa-comments' },
}

/**
 * The bar during a reveal. `items` is the bar the server sends now (with the
 * new tab); phase 'before' is the bar as it was, 'room' makes room for a tab
 * still to be born, 'arrived' is the new bar with the arrival animated.
 */
export function revealBar (items, reveal, phase, { mystic = null } = {}) {
  if (!reveal || !phase || !Array.isArray(items)) return items
  const spec = specFor(reveal)
  if (!spec) return items
  const at = items.findIndex((i) => i.key === spec.to)
  if (at === -1) return items
  const out = items.map((i) => ({ ...i }))
  if (spec.change === 'born') {
    if (phase === 'before') return out.filter((_, i) => i !== at)
    if (phase === 'room') out[at].pending = true
    else out[at].anim = 'born'
    out[at].slot = spec.to
    return out
  }
  if (spec.change === 'ring') {
    if (phase === 'arrived') out[at].anim = 'ring'
    out[at].slot = spec.to
    return out
  }
  if (phase === 'arrived') { out[at].anim = 'upgr'; out[at].slot = spec.to; return out }
  // The old face, still in the slot the light will land on.
  const old = spec.from
  const face = old === 'mystic'
    ? { key: 'mystic', label: (mystic && mystic.label) || 'Mystic AIda', icon: '', portrait: (mystic && mystic.portrait) || '', sub: (mystic && mystic.sub) || 'iam' }
    : { key: old, label: FACES[old].label, icon: FACES[old].icon, portrait: '' }
  out[at] = { ...out[at], ...face, dot: false, slot: spec.to }
  return out
}

/** Where "Get started" lands: inside the new room, never the Quest. */
export function landingFor (reveal) {
  switch (reveal && reveal.key) {
    case 'fs': {
      const r = reveal.reading
      const id = r && typeof r === 'object' ? r.id : r
      return id ? { type: 'journal', reading: String(id), reader: (r && r.reader) || '' } : { type: 'journal' }
    }
    case 'ri': return { type: 'mirror' }
    case 've': return { type: 'circle' }
    case 'zw': return { type: 'community' }
    case 'mo': return { type: 'altar-ritual' }
    case 'su': return { type: 'studio-entry' }
    default: return null
  }
}

function andList (names) {
  const n = (names || []).filter(Boolean)
  if (n.length <= 1) return n.join('')
  return `${n.slice(0, -1).join(', ')} and ${n[n.length - 1]}`
}

/**
 * The hero over the new room (prototype HERO), from what the reveal carries.
 * Bodies may bold with **…**; the app renders them as segments.
 */
export function heroFor (reveal) {
  const r = reveal || {}
  switch (r.key) {
    case 'fs': return {
      eyebrow: 'First Star', title: 'Your Journal', art: 'fs',
      body: ['Every reading you keep lives here, with what landed and how it felt.', 'Come back to any of them, whenever you like.'],
    }
    case 'ri': {
      const sg = r.signs || {}
      const parts = [sg.sun && `Sun in ${sg.sun}`, sg.moon && `Moon in ${sg.moon}`, sg.rising && `${sg.rising} rising`].filter(Boolean)
      const chart = parts.length ? `: **${parts.join(', ')}**` : ''
      return {
        eyebrow: 'The Rising', title: r.name || 'Your Mirror', art: 'portrait', portrait: r.portrait || '',
        body: [`This is your Mirror. Your sky gave you this name, and a portrait painted from your chart${chart}.`,
          'Your first conjured look is waiting in Looks. Change any of it, any time.'],
      }
    }
    case 've': {
      const from = r.from || (r.gift && r.gift.from) || ''
      const here = from ? `**${from} is already here.** ${from} read for you first.`
        : (r.people && r.people.length ? `${andList(r.people)} ${r.people.length === 1 ? 'is' : 'are'} here.` : '')
      return {
        eyebrow: 'Venus', title: 'Your Circle', art: 've',
        body: [`The people you read for, and the people who read for you.${here ? ' ' + here : ''}`,
          'When someone writes, you’ll find it under Messages, in your menu.'],
        quiet: 'In time, you can ask up to 13 of them into your coven, the few who see your real readings. Each one chooses.',
      }
    }
    case 'zw': return {
      eyebrow: 'Zodiac Wheel', title: 'The Community', art: 'zw',
      body: [`**Lessons**: what readings taught other seekers, in their own words.${r.lesson_from ? ` ${r.lesson_from}’s is saved for you.` : ''}`,
        `**Mystics**: every voice that reads here. ${(r.mystic && r.mystic.label) || 'Mystic AIda'} lives here now.`,
        '**Voice**: where everyone helps choose what comes next.'],
    }
    case 'mo': return {
      eyebrow: 'The Moon', title: 'Your Altar', art: 'mo',
      body: ['Your first horoscope is here, read from your own chart. A new one arrives each morning.',
        'Your Mirror and Journal live here now too, beside it.'],
    }
    case 'su': {
      const m = r.mystic && r.mystic.name
      return {
        eyebrow: 'The Sun', title: 'Your Studio', art: 'square', portrait: (r.mystic && r.mystic.portrait) || '',
        body: [m ? `**${m}** was drawn from your chart, the way your own name was.` : 'Your own Mystic is waiting to be made, drawn from your chart.',
          'Tune its voice, choose how it reads, then share it with the people you read for.'],
      }
    }
    default: return null
  }
}

// When the light leaves the art (s) and how long reduced motion holds (s): the prototype's T.
const T = { fs: [2.7, 1.6], ri: [5.6, 2.0], ve: [4.6, 1.8], zw: [5.0, 1.8], mo: [5.2, 2.0], su: [5.6, 2.0] }
/** ms: when the light leaves, when the art fades, when the hero rises. */
export function revealTiming (key, reduced = false) {
  const t = T[key] || [3, 1.6]
  const light = Math.round((reduced ? t[1] : t[0]) * 1000)
  const fade = light + (reduced ? 700 : 1500)
  return { light, fade, hero: fade + (reduced ? 200 : 350) }
}
